import React from 'react';
import { PushpinFilled } from '@ant-design/icons';
import { ColumnType } from './types';
import { isGroup } from './settings/columns';
import cn from './styles.module.scss';

/**
 * F3 pin indicator. Renders a pin glyph on the header of pinned columns:
 * on a pinned leaf, and on a group header only when the whole group is pinned.
 *
 * Activation is the Table `showPinIndicators` prop — an explicit per-table
 * opt-in, false by default, so every existing table renders exactly as before
 * until the app migrates it to Table Settings v2 and flips the prop (design
 * §12.3's no-op-by-default guarantee, held by the default value instead of a
 * meta-presence heuristic).
 *
 * Pinned detection uses resolver output: `fixed === 'left'` on a non-system
 * node — a leaf, or a group node fixed as one atomic unit (a depth-≥3
 * sub-group per the atomic rule in settings, or a consumer-fixed whole group).
 * `fixed: 'right'` (system/actions) is never a pin. Pure and
 * reference-preserving: columns without a pinned node are returned as-is.
 */

// P3 design-QA toggle: when a whole group is pinned, show the group glyph only
// and suppress the per-leaf glyphs inside it. Flip to `false` to show both.
const SUPPRESS_LEAF_GLYPH_IN_FULL_GROUP = true;

function isPinnedNode<R>(col: ColumnType<R>): boolean {
  return col.fixed === 'left' && col.system !== true;
}

function withGlyph<R>(col: ColumnType<R>): ColumnType<R>['title'] {
  const { title } = col;
  const glyph = <PushpinFilled className={cn.pinIndicator} />;
  if (typeof title === 'function') {
    const render = title;
    return (props) => (
      <span className={cn.pinnedHeader}>
        {render(props)}
        {glyph}
      </span>
    );
  }
  return (
    <span className={cn.pinnedHeader}>
      {title}
      {glyph}
    </span>
  );
}

function decorateGroup<R>(col: ColumnType<R>): ColumnType<R> {
  const children = col.children ?? [];
  const wholeGroupPinned = children.length > 0 && children.every(isPinnedNode);
  if (wholeGroupPinned) {
    const nextChildren = SUPPRESS_LEAF_GLYPH_IN_FULL_GROUP ? children : children.map(decorateColumn);
    return { ...col, title: withGlyph(col), children: nextChildren };
  }
  const nextChildren = children.map(decorateColumn);
  return nextChildren.some((child, i) => child !== children[i])
    ? { ...col, children: nextChildren }
    : col;
}

function decorateColumn<R>(col: ColumnType<R>): ColumnType<R> {
  // Node-level pin wins: a pinned leaf, or a pinned atomic unit — a group node
  // the resolver fixed as one thing. One glyph on the node's own header; its
  // children render as authored (they carry no fixed of their own).
  if (isPinnedNode(col)) return { ...col, title: withGlyph(col) };
  return isGroup(col) ? decorateGroup(col) : col;
}

/**
 * Add pin glyphs to the headers of pinned columns. Activation is gated by the
 * Table `showPinIndicators` prop at the call site (see module doc).
 * Reference-preserving: returns the input array untouched when nothing is
 * pinned.
 */
export function withPinIndicators<R>(columns: ColumnType<R>[]): ColumnType<R>[] {
  const decorated = columns.map(decorateColumn);
  return decorated.some((col, i) => col !== columns[i]) ? decorated : columns;
}

/**
 * Label-only clone seam (dormant under model C). When design picks a split
 * render model (B1/B2, design §5), a cloned group header must render plain
 * text — `pinnedGroupTitle`, else a string `title`, else the `key` — never the
 * custom `title` ReactNode, so interactive header controls are not duplicated
 * across the clone (design §5.2, §5.3). `editTitle` is deliberately NOT
 * consulted: it names the column in the settings panel, a separate concern
 * from the rendered header. Not wired into rendering under model C; kept ready
 * and unit-tested for the future split models.
 */
export function getClonedHeaderLabel<R>(col: ColumnType<R>): string {
  if (col.pinnedGroupTitle) return col.pinnedGroupTitle;
  if (typeof col.title === 'string') return col.title;
  return col.key;
}
